import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  Truck,
  Euro,
  ArrowRight,
  Sparkles,
  Target,
  Mail,
  BarChart3,
  Activity,
  Calendar,
  Clock,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { customers } from '@/lib/api'
import { cn } from '@/lib/utils'

// Componente de tarjeta de estadística
function StatCard({
  title,
  value,
  change,
  changeType = 'neutral',
  icon: Icon,
  iconColor,
  iconBg,
}: {
  title: string
  value: string | number
  change?: string
  changeType?: 'up' | 'down' | 'neutral'
  icon: any
  iconColor: string
  iconBg: string
}) {
  return (
    <div className="stat-card group hover:shadow-soft-lg transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className="space-y-3">
          <p className="stat-label">{title}</p>
          <p className="stat-value">{value}</p>
          {change && (
            <div
              className={cn(
                'stat-change',
                changeType === 'up' && 'stat-change-up',
                changeType === 'down' && 'stat-change-down',
                changeType === 'neutral' && 'text-slate-500'
              )}
            >
              {changeType === 'up' && <TrendingUp className="w-3.5 h-3.5" />}
              {changeType === 'down' && <TrendingDown className="w-3.5 h-3.5" />}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-2xl transition-transform group-hover:scale-110',
            iconBg
          )}
        >
          <Icon className={cn('w-6 h-6', iconColor)} />
        </div>
      </div>
    </div>
  )
}

// Componente de acción rápida
function QuickActionCard({
  title,
  description,
  icon: Icon,
  href,
  gradient,
  delay = 0,
}: {
  title: string
  description: string
  icon: any
  href: string
  gradient: string
  delay?: number
}) {
  return (
    <Link
      to={href}
      className="group card card-interactive p-5 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3',
            gradient
          )}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-slate-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
      </div>
    </Link>
  )
}

// Componente de segmento RFM
function RfmSegmentBar({
  segment,
  count,
  total,
  color,
}: {
  segment: string
  count: number
  total: number
  color: string
}) {
  const percentage = total > 0 ? (count / total) * 100 : 0

  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{segment}</span>
        <span className="text-sm font-semibold text-slate-900 dark:text-white">{count}</span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500 ease-out', color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const customerStats = useQuery({
    queryKey: ['customer-stats'],
    queryFn: () => customers.getStats(),
  })

  const stats = customerStats.data
  const totalSegments = stats?.segment_distribution
    ? Object.values(stats.segment_distribution).reduce((a: number, b: any) => a + (b as number), 0)
    : 0

  // Colores para segmentos RFM
  const segmentColors: Record<string, string> = {
    Champions: 'bg-success-500',
    'Loyal Customers': 'bg-primary-500',
    'Potential Loyalist': 'bg-accent-500',
    'New Customers': 'bg-cyan-500',
    Promising: 'bg-teal-500',
    'Need Attention': 'bg-warning-500',
    'About To Sleep': 'bg-orange-500',
    'At Risk': 'bg-danger-400',
    "Can't Lose Them": 'bg-danger-500',
    Hibernating: 'bg-slate-400',
    Lost: 'bg-slate-500',
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Bienvenido de nuevo. Aqui tienes un resumen de tu negocio.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Clock className="w-4 h-4" />
            <span>
              Ultima actualizacion:{' '}
              {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          title="Clientes Totales"
          value={stats?.total_customers?.toLocaleString() || '...'}
          change="+12% este mes"
          changeType="up"
          icon={Users}
          iconColor="text-primary-600 dark:text-primary-400"
          iconBg="bg-primary-100 dark:bg-primary-900/30"
        />
        <StatCard
          title="Clientes Activos"
          value={stats?.active_customers?.toLocaleString() || '...'}
          change="+5% esta semana"
          changeType="up"
          icon={Activity}
          iconColor="text-success-600 dark:text-success-400"
          iconBg="bg-success-100 dark:bg-success-900/30"
        />
        <StatCard
          title="Suscriptores"
          value={stats?.newsletter_subscribers?.toLocaleString() || '...'}
          change="+23 nuevos"
          changeType="up"
          icon={Mail}
          iconColor="text-accent-600 dark:text-accent-400"
          iconBg="bg-accent-100 dark:bg-accent-900/30"
        />
        <StatCard
          title="Ingresos Totales"
          value={stats?.total_revenue ? `${stats.total_revenue.toLocaleString('es-ES', { maximumFractionDigits: 0 })}` : '...'}
          icon={Euro}
          iconColor="text-warning-600 dark:text-warning-400"
          iconBg="bg-warning-100 dark:bg-warning-900/30"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-4">
          <div className="section-header">
            <h2 className="section-title">Acciones Rapidas</h2>
            <p className="section-description">Accede rapidamente a las funciones mas usadas</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <QuickActionCard
              title="Crear Producto con IA"
              description="Genera fichas de producto optimizadas automaticamente"
              icon={Sparkles}
              href="/products/create"
              gradient="bg-gradient-to-br from-primary-500 to-accent-600"
              delay={0}
            />
            <QuickActionCard
              title="Segmentacion RFM"
              description="Analiza y segmenta clientes por comportamiento"
              icon={Users}
              href="/customers"
              gradient="bg-gradient-to-br from-success-500 to-teal-600"
              delay={50}
            />
            <QuickActionCard
              title="Campanas Marketing"
              description="Crea y gestiona campanas de email marketing"
              icon={Target}
              href="/marketing"
              gradient="bg-gradient-to-br from-danger-500 to-orange-500"
              delay={100}
            />
            <QuickActionCard
              title="Gestionar Transportistas"
              description="Configura horarios y activacion automatica"
              icon={Truck}
              href="/carriers"
              gradient="bg-gradient-to-br from-cyan-500 to-blue-600"
              delay={150}
            />
          </div>
        </div>

        {/* RFM Segments */}
        <div className="space-y-4">
          <div className="section-header">
            <h2 className="section-title">Segmentos RFM</h2>
            <Link
              to="/customers"
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Ver todo
            </Link>
          </div>

          <div className="card p-5">
            {stats?.segment_distribution ? (
              <div className="space-y-4">
                {Object.entries(stats.segment_distribution)
                  .sort((a, b) => (b[1] as number) - (a[1] as number))
                  .slice(0, 6)
                  .map(([segment, count]) => (
                    <RfmSegmentBar
                      key={segment}
                      segment={segment}
                      count={count as number}
                      total={totalSegments}
                      color={segmentColors[segment] || 'bg-slate-400'}
                    />
                  ))}
                {Object.keys(stats.segment_distribution).length > 6 && (
                  <p className="text-xs text-center text-slate-500 dark:text-slate-400 pt-2">
                    +{Object.keys(stats.segment_distribution).length - 6} segmentos mas
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between">
                      <div className="skeleton skeleton-text w-24" />
                      <div className="skeleton skeleton-text w-8" />
                    </div>
                    <div className="skeleton h-2 rounded-full" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 dark:text-white">Actividad Reciente</h3>
            <span className="badge badge-primary">Hoy</span>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {[
                {
                  icon: Package,
                  text: '15 productos actualizados via Factusol',
                  time: 'Hace 2 horas',
                  color: 'text-primary-500',
                  bg: 'bg-primary-100 dark:bg-primary-900/30',
                },
                {
                  icon: Users,
                  text: '3 nuevos clientes registrados',
                  time: 'Hace 4 horas',
                  color: 'text-success-500',
                  bg: 'bg-success-100 dark:bg-success-900/30',
                },
                {
                  icon: Mail,
                  text: 'Campana "Black Friday" enviada',
                  time: 'Ayer',
                  color: 'text-accent-500',
                  bg: 'bg-accent-100 dark:bg-accent-900/30',
                },
                {
                  icon: Truck,
                  text: 'Transportista MRW activado',
                  time: 'Ayer',
                  color: 'text-cyan-500',
                  bg: 'bg-cyan-100 dark:bg-cyan-900/30',
                },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={cn('flex items-center justify-center w-8 h-8 rounded-lg', item.bg)}>
                    <item.icon className={cn('w-4 h-4', item.color)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 dark:text-slate-300">{item.text}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upcoming Tasks */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 dark:text-white">Tareas Programadas</h3>
            <Link
              to="/scheduler"
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Ver todas
            </Link>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {[
                {
                  name: 'Sincronizacion Factusol',
                  time: '08:00',
                  status: 'active',
                  frequency: 'Diario',
                },
                {
                  name: 'Actualizacion Google Ads',
                  time: '12:00',
                  status: 'active',
                  frequency: 'Diario',
                },
                {
                  name: 'Backup Base de Datos',
                  time: '03:00',
                  status: 'active',
                  frequency: 'Diario',
                },
                {
                  name: 'Limpieza Cache',
                  time: '00:00',
                  status: 'paused',
                  frequency: 'Semanal',
                },
              ].map((task, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-2 h-2 rounded-full',
                        task.status === 'active' ? 'bg-success-500' : 'bg-slate-400'
                      )}
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{task.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{task.frequency}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{task.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
